import generateSHA256Hash from '@utils/crypto';

import { resolveMemberFieldIdentity } from '../components/wizard-steps/fields-step/helpers/memberFieldPicker';
import { resolveCoverImageCredentials } from '../components/wizard-steps/setup-step/helpers/coverImageFile';
import {
  FormFieldData,
  FormSetupData,
  FormWizardData,
  StartCollectionFrom
} from '../types';
import {
  FormCreateDiscountConfigApi,
  FormCreateFieldApi,
  FormCreateImageApi,
  FormCreateRequestApi
} from '../types/formCreateApi';
import { toFormCloseDateIso } from './formDetailDisplay';
import { mapFormFieldsToCreateApi } from './sortFormFieldsForApiOrder';

export const mapStartCollectionFrom = (
  value?: StartCollectionFrom
): string | undefined => {
  if (!value) return undefined;
  return value === 'same_month' ? 'current_month' : 'next_month';
};

const resolveSyncToMember = (field: FormFieldData): boolean => {
  if (field.fieldCategory === 'basic' || field.isLocked) {
    return true;
  }
  if (field.fieldCategory === 'member') {
    return field.syncToMember ?? true;
  }
  return field.syncToMember ?? false;
};

export const mapFormFieldToCreateApi = (
  field: FormFieldData,
  displayOrder?: number
): FormCreateFieldApi => {
  const resolved = resolveMemberFieldIdentity(field);

  return {
    name: resolved.label,
    type: resolved.type,
    is_required: resolved.required,
    display_order: displayOrder ?? resolved.order,
    options: resolved.options ?? [],
    sync_to_member: resolveSyncToMember(resolved)
  };
};

export const mapDiscountConfig = (
  setup: FormSetupData
): FormCreateDiscountConfigApi => {
  const isEnabled = setup.applyDiscount ?? false;
  if (!isEnabled) {
    return { is_enabled: false };
  }

  return {
    is_enabled: true,
    type: setup.discountType ?? 'flat',
    value: setup.discountValue ?? 0
  };
};

export const mapCoverImage = async (
  coverImageFile?: FormSetupData['coverImageFile']
): Promise<FormCreateImageApi | undefined> => {
  if (!coverImageFile?.fileId || !coverImageFile?.accessKey) {
    return undefined;
  }

  const signature =
    coverImageFile.signature ??
    (await generateSHA256Hash(
      `${coverImageFile.fileId}|${coverImageFile.accessKey}`
    ));

  return {
    file_id: coverImageFile.fileId,
    access_key: coverImageFile.accessKey,
    signature
  };
};

export const appendCoverImageToRequest = async (
  request: FormCreateRequestApi,
  setup: FormSetupData
) => {
  const image = await mapCoverImage(
    resolveCoverImageCredentials(setup) ?? undefined
  );

  if (image) {
    request.image = image;
    return;
  }

  if (setup.coverImageUrl) {
    request.image_url = setup.coverImageUrl;
  }
};

export const mapWizardToCreateFormRequest = async (
  wizardData: FormWizardData
): Promise<FormCreateRequestApi> => {
  const { setup, fields } = wizardData;
  const isGroupMode = setup.addToMode === 'group';

  const request: FormCreateRequestApi = {
    name: setup.name,
    description: setup.description ?? '',
    add_to_mode: setup.addToMode,
    fee_amount: setup.feeAmount ?? 0,
    is_partial_payment_enabled: setup.acceptInstallments ?? false,
    discount_config: mapDiscountConfig(setup),
    form_fields: mapFormFieldsToCreateApi(fields, mapFormFieldToCreateApi)
  };

  await appendCoverImageToRequest(request, setup);
  if (isGroupMode && setup.groupId) {
    request.group_id = setup.groupId;
  }
  if (isGroupMode && setup.startCollectionFrom) {
    request.start_collection_from = mapStartCollectionFrom(
      setup.startCollectionFrom
    );
  }
  if (setup.maxRegistrations !== undefined) {
    request.registration_max_count = setup.maxRegistrations;
  }
  if (setup.autoCloseDate) {
    request.registration_end_time = toFormCloseDateIso(
      new Date(setup.autoCloseDate)
    );
  }
  if (setup.acceptInstallments && setup.minPartialPaymentAmount !== undefined) {
    request.min_partial_payment_amount = setup.minPartialPaymentAmount;
  }

  return request;
};
